import { useState, useRef, useEffect } from 'react'
import { Pressable } from '@ayble-health/dashboard-components'
import { Chip } from 'components/ui/chip'
import type { Medication } from '../../models/careOSPrototype'
import { ChevronDown, DotsThreeIcon, FlagPennantIcon } from './icons'

// One row in the Pharmacy med list. Compact header row plus an optional
// expanded detail panel — these lists run long (the reference patient has
// 14), so the collapsed state stays scannable: name, dose, prescribed/added
// date, and only the badges that carry a signal.
//
// The kebab menu follows LabsTab's ResultActionsMenu pattern (outside-mousedown
// close, click propagation stopped, danger item tinted fuschia).

export type MedicationAction = 'archive' | 'restore'

function KindChip({ label }: { label: string }) {
  return <Chip variant="neutral">{label}</Chip>
}

function ActionsMenu({ med, onAction }: {
  med: Medication
  onAction: (action: MedicationAction) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const items: { label: string; action: MedicationAction; danger: boolean }[] =
    med.status === 'archived'
      ? [{ label: 'Move back to current', action: 'restore', danger: false }]
      : [{ label: 'No longer taking', action: 'archive', danger: true }]

  const choose = (action: MedicationAction) => {
    setMenuOpen(false)
    onAction(action)
  }

  return (
    <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
      <Pressable
        className="hover-seafoam"
        aria-label={`Actions for ${med.name}`}
        onClick={() => setMenuOpen(o => !o)}
        color="steel"
        p="2px"
        borderRadius="sm"
        flexDirection="row"
        alignItems="center"
        transition="color 0.15s"
      >
        <DotsThreeIcon />
      </Pressable>
      {menuOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0,
          background: 'var(--canvas)', border: '1px solid var(--cloud)',
          borderRadius: 'var(--radius-card)',
          boxShadow: '0 4px 12px rgba(4,51,56,0.10)', padding: '4px 0',
          minWidth: 180, zIndex: 30,
        }}>
          {items.map(({ label, action, danger }) => (
            <Pressable
              key={label}
              onClick={() => choose(action)}
              w="100%"
              textAlign="left"
              px={3}
              py="7px"
              fontSize="xs"
              fontFamily="body"
              color={danger ? 'fuschia' : 'evergreen'}
              transition="background 0.1s"
              _hover={{
                bg: danger ? 'var(--row-hover-danger)' : 'var(--row-hover)',
              }}
            >
              {label}
            </Pressable>
          ))}
        </div>
      )}
    </div>
  )
}

const captionStyle: React.CSSProperties = {
  fontSize: 'var(--text-caption-size)', fontWeight: 'var(--text-caption-weight)',
  letterSpacing: 'var(--text-caption-tracking)', textTransform: 'uppercase',
  color: 'var(--blue-grey)', fontFamily: 'var(--font-body)', margin: '0 0 4px',
}

function DetailColumn({ label, lines }: { label: string; lines: string[] }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={captionStyle}>{label}</p>
      {lines.map((line, i) => (
        <p key={i} style={{
          fontSize: 'var(--text-xs-size)', color: 'var(--evergreen)',
          fontFamily: 'var(--font-body)', margin: 0, lineHeight: 1.4,
        }}>
          {line}
        </p>
      ))}
    </div>
  )
}

function ExpandedDetail({ med }: { med: Medication }) {
  const hasDetailRow = med.prescriber || med.pharmacy || med.refills
  const hasAnything = med.instructions || hasDetailRow || med.notes

  return (
    <div style={{
      marginTop: 11, paddingTop: 11, borderTop: '1px solid var(--cloud)',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      {!hasAnything && (
        <p style={{
          fontSize: 'var(--text-xs-size)', color: 'var(--steel)',
          fontFamily: 'var(--font-body)', margin: 0,
        }}>
          No additional details recorded
        </p>
      )}

      {med.instructions && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          background: 'var(--mint)', borderRadius: 'var(--radius-card)',
          padding: '10px 12px',
        }}>
          <span style={{ color: 'var(--seafoam)', flexShrink: 0, marginTop: 2 }}>
            <FlagPennantIcon />
          </span>
          <div>
            <p style={{ ...captionStyle, color: 'var(--seafoam)', margin: '0 0 3px' }}>Instructions</p>
            <p style={{
              fontSize: 'var(--text-xs-size)', color: 'var(--evergreen)',
              fontFamily: 'var(--font-body)', margin: 0, lineHeight: 1.45,
            }}>
              {med.instructions}
            </p>
          </div>
        </div>
      )}

      {hasDetailRow && (
        <div style={{ display: 'flex', gap: 20 }}>
          {med.prescriber && (
            <DetailColumn label="Prescriber" lines={[med.prescriber.name, med.prescriber.specialty]} />
          )}
          {med.pharmacy && (
            <DetailColumn label="Pharmacy" lines={[med.pharmacy.name, med.pharmacy.address]} />
          )}
          {med.refills && (
            <DetailColumn
              label="Refills"
              lines={[
                `${med.refills.remaining} of ${med.refills.total} remaining`,
                ...(med.refills.nextDue ? [`Next due ${med.refills.nextDue}`] : []),
              ]}
            />
          )}
        </div>
      )}

      {med.notes && (
        <div style={{ borderLeft: '2px solid var(--cloud)', paddingLeft: 10 }}>
          <p style={captionStyle}>Clinician note</p>
          <p style={{
            fontSize: 'var(--text-xs-size)', color: 'var(--evergreen)',
            fontFamily: 'var(--font-body)', margin: 0, lineHeight: 1.45,
          }}>
            {med.notes}
          </p>
        </div>
      )}
    </div>
  )
}

export default function MedicationRow({ med, onAction }: {
  med: Medication
  onAction: (action: MedicationAction) => void
}) {
  const [hovered, setHovered] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const archived = med.status === 'archived'

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setExpanded(e => !e)}
      style={{
        padding: '11px 12px 11px 14px',
        border: '1px solid',
        borderColor: hovered && !archived ? 'var(--sage)' : 'var(--cloud)',
        borderRadius: 'var(--radius-card)',
        background: archived ? 'var(--bg-subtle)' : 'var(--canvas)',
        transition: 'border-color 0.15s',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Name + dose + prescribed/added date */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            fontSize: 'var(--text-sm-b-size)', fontWeight: 'var(--text-sm-b-weight)',
            color: archived ? 'var(--blue-grey)' : 'var(--evergreen)',
            fontFamily: 'var(--font-body)', margin: 0, lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {med.name}
          </p>
          <p style={{
            fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)',
            fontFamily: 'var(--font-body)', margin: '2px 0 0', lineHeight: 1.3,
          }}>
            {archived
              ? `Stopped ${med.archivedDate ?? '—'}${med.dose ? ` · ${med.dose}` : ''}`
              : med.dose || 'No dose recorded'}
          </p>
          {!archived && (
            <p style={{
              fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)',
              fontFamily: 'var(--font-body)', margin: '2px 0 0', lineHeight: 1.3,
            }}>
              {med.kind === 'supplement' ? 'Added' : 'Prescribed'} {med.addedDate}
            </p>
          )}
        </div>

        {/* Badges — the kind chip is only needed once "no longer taking" mixes
            e-prescriptions, medications, and supplements back together; within
            their own active sections the heading already says which is which. */}
        {archived && med.kind === 'supplement' && <KindChip label="Supplement" />}

        <span style={{
          display: 'inline-flex', color: 'var(--steel)', flexShrink: 0,
          transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s',
        }}>
          <ChevronDown />
        </span>

        <ActionsMenu med={med} onAction={onAction} />
      </div>

      {expanded && <ExpandedDetail med={med} />}
    </div>
  )
}
